// Smart Inbox categorization (frontend mirror of
// backend/services/mailCategoryService.js).
//
// The backend already stamps each inbox mail with a `category`, but we keep an
// identical client-side classifier as a fallback so the UI stays correct for
// older/cached mails or responses that don't carry the field yet.

export const MAIL_CATEGORIES = ['primary', 'social', 'promotions', 'updates', 'forums'];

// Tab metadata: order, label and a compact inline SVG icon path.
export const CATEGORY_TABS = [
  { id: 'primary', label: 'Primary', icon: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' },
  { id: 'social', label: 'Social', icon: 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z' },
  { id: 'promotions', label: 'Promotions', icon: 'M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58s1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41s-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z' },
  { id: 'updates', label: 'Updates', icon: 'M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z' },
  { id: 'forums', label: 'Forums', icon: 'M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z' },
];

const SOCIAL_DOMAINS = [
  'facebook.com', 'facebookmail.com', 'fb.com', 'twitter.com', 'x.com',
  'linkedin.com', 'linkedinmail.com', 'instagram.com', 'pinterest.com',
  'youtube.com', 'tiktok.com', 'reddit.com', 'redditmail.com', 'snapchat.com',
  'quora.com', 'meetup.com', 'tumblr.com', 'discord.com', 'twitch.tv',
  'nextdoor.com', 'medium.com',
];

const SOCIAL_KEYWORDS = [
  'tagged you', 'commented on', 'liked your', 'sent you a friend request',
  'wants to connect', 'started following', 'new follower', 'mentioned you',
  'invitation to connect', 'endorsed you',
];

const PROMO_LOCALPARTS = [
  'marketing', 'promo', 'promotions', 'deals', 'offers', 'offer',
  'newsletter', 'news', 'sales', 'campaign', 'campaigns', 'email',
];

const PROMO_KEYWORDS = [
  'sale', '% off', 'discount', 'deal', 'coupon', 'promo', 'save up to',
  'clearance', 'newsletter', 'limited time', 'free shipping', 'exclusive',
  'flash sale', 'buy now', 'shop now', 'lowest price', 'special offer',
  'bogo', 'cyber monday', 'black friday', 'gift card',
];

const UPDATE_LOCALPARTS = [
  'noreply', 'no-reply', 'no_reply', 'donotreply', 'do-not-reply',
  'notifications', 'notification', 'notify', 'alerts', 'alert',
  'receipts', 'receipt', 'billing', 'invoice', 'invoices', 'orders', 'order',
  'confirm', 'confirmation', 'verify', 'verification', 'security',
  'account', 'accounts', 'support', 'help', 'mailer-daemon', 'postmaster',
  'auto-confirm', 'bounce', 'delivery', 'system',
];

const UPDATE_KEYWORDS = [
  'receipt', 'invoice', 'your order', 'order confirmation', 'order #',
  'confirmation', 'verify your', 'verification code', 'one-time', 'otp',
  'your code', 'password reset', 'statement', 'has shipped', 'out for delivery',
  'delivered', 'payment', 'booking', 'reservation', 'ticket', 'appointment',
  'security alert', 'sign-in', 'signed in', 'new login',
];

const FORUM_LOCALPARTS = ['list', 'lists', 'group', 'groups', 'forum', 'forums'];

const lc = (value) => String(value || '').toLowerCase();

const extractEmail = (from) => {
  const raw = String(from || '').trim();
  const angle = raw.match(/<([^>]+)>/);
  if (angle) return angle[1].trim().toLowerCase();
  const email = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return email ? email[0].toLowerCase() : '';
};

const domainMatches = (domain, candidate) =>
  domain === candidate || domain.endsWith(`.${candidate}`);

const includesAny = (haystack, needles) => needles.some((n) => haystack.includes(n));

// Client-side fallback classifier (subject/sender only; headers rarely present).
export const classifyMailCategory = (mail = {}) => {
  const email = extractEmail(mail.from);
  const at = email.indexOf('@');
  const localPart = at === -1 ? '' : email.slice(0, at);
  const domain = at === -1 ? '' : email.slice(at + 1);
  const subject = lc(mail.subject);

  if (domain && SOCIAL_DOMAINS.some((d) => domainMatches(domain, d))) return 'social';
  if (includesAny(subject, SOCIAL_KEYWORDS)) return 'social';

  if (domainMatches(domain, 'googlegroups.com') || FORUM_LOCALPARTS.includes(localPart)) {
    return 'forums';
  }

  if (includesAny(subject, PROMO_KEYWORDS)) return 'promotions';
  if (PROMO_LOCALPARTS.some((part) => localPart.includes(part))) return 'promotions';

  if (UPDATE_LOCALPARTS.some((part) => localPart.includes(part))) return 'updates';
  if (includesAny(subject, UPDATE_KEYWORDS)) return 'updates';

  return 'primary';
};

// Prefer the server-provided category; fall back to local classification.
export const getMailCategory = (mail = {}) => {
  const category = String(mail.category || '').toLowerCase();
  if (MAIL_CATEGORIES.includes(category)) return category;
  return classifyMailCategory(mail);
};
